// =============================================================================
// src/features/reputation/reputation-takeover.ts
//
// Orquestrador do takeover #reputationTakeover: abrir/fechar (FAB, ESC, "voltar
// ao mapa"), focus-trap, init preguiçoso dos charts e switch de seções.
// Porte FIEL do IIFE de assets/reputation.js (a "casca": linhas 12-22, 431-510):
//   views/scrollEl/segBtns 432-458, drawCharts 461-469, visibleFocusables 472-476,
//   openReputation 477-488, closeReputation 489-496, listeners 497-509.
//
// `reduceMotion` é lido UMA vez no init (matchMedia), igual ao legado (linha 19).
// SRP: so o ciclo de vida do takeover; charts/trend/duel/ticker vivem em modulos.
// =============================================================================

import type { AppContext } from '../../app/types';
import { qs, qsa } from '../../app/dom';
import { drawReputationChannels, drawReputationDonut, drawReputationGauge, drawReputationNeg, drawReputationRank } from './reputation-charts';
import { drawReputationTrend } from './reputation-trend';
import { initReputationDuel } from './reputation-duel';
import { bindReputationTickerToggle, buildReputationTicker } from './reputation-ticker';
import { REPUTATION_NEG } from './reputation-data';

/** Chaves das tres views do takeover (data-rep-view). */
type ReputationViewKey = 'overview' | 'compare' | 'sources';

/** Estado de ciclo de vida do takeover, resolvido no init (substitui as vars do IIFE). */
interface ReputationTakeoverState {
  readonly takeover: HTMLElement;
  readonly launcher: HTMLElement;
  readonly reduceMotion: boolean;
  readonly views: Record<ReputationViewKey, HTMLElement | null>;
  readonly scrollEl: HTMLElement | null;
  readonly segBtns: readonly HTMLElement[];
  charted: boolean;
  lastFocus: HTMLElement | null;
}

/**
 * Liga o comportamento do takeover de Reputacao. Chamado por main.ts apos a
 * marcacao estar montada (substitui o auto-boot do IIFE legado). Idempotente: se
 * launcher/takeover faltarem, retorna cedo (igual a guarda da linha 17 do legado).
 *
 * @example
 *   initReputationTakeover(ctx); // em main.ts, depois de mountRouteLayer()
 */
export function initReputationTakeover(_ctx: AppContext): void {
  const takeover = qs<HTMLElement>('#reputationTakeover');
  const launcher = qs<HTMLElement>('#reputationLauncher');
  if (!takeover || !launcher) return;

  const state: ReputationTakeoverState = {
    takeover,
    launcher,
    reduceMotion: window.matchMedia('(prefers-reduced-motion: reduce)').matches,
    views: resolveViews(),
    scrollEl: qs<HTMLElement>('.rep-scroll', takeover),
    segBtns: qsa<HTMLElement>('.rep-seg-btn', takeover),
    charted: false,
    lastFocus: null,
  };

  bindViewSwitch(state);
  bindTickerControls(state);
  bindOpenClose(state);
}

/** Resolve as tres views — porte de `views` (reputation.js 432-436). */
function resolveViews(): Record<ReputationViewKey, HTMLElement | null> {
  return {
    overview: qs<HTMLElement>('#repViewOverview'),
    compare: qs<HTMLElement>('#repViewCompare'),
    sources: qs<HTMLElement>('#repViewSources'),
  };
}

/** Troca a view ativa — porte FIEL de `setView` (reputation.js 440-450). */
function setView(state: ReputationTakeoverState, requested: string): void {
  const v: ReputationViewKey = isViewKey(requested) && state.views[requested] ? requested : 'overview';
  (Object.keys(state.views) as ReputationViewKey[]).forEach((k) => {
    const el = state.views[k];
    if (el) el.hidden = k !== v;
  });
  state.segBtns.forEach((b) => {
    const on = b.dataset['repView'] === v;
    b.classList.toggle('active', on);
    b.setAttribute('aria-pressed', on ? 'true' : 'false');
  });
  if (v === 'compare') {
    drawReputationRank();
    initReputationDuel();
  }
  if (state.scrollEl) state.scrollEl.scrollTo({ top: 0, behavior: state.reduceMotion ? 'auto' : 'smooth' });
}

/** Narrowing de string para ReputationViewKey. */
function isViewKey(v: string): v is ReputationViewKey {
  return v === 'overview' || v === 'compare' || v === 'sources';
}

/** Liga seg-botoes + botoes-ponte [data-rep-go] — porte de reputation.js 451-458. */
function bindViewSwitch(state: ReputationTakeoverState): void {
  state.segBtns.forEach((b) => {
    b.setAttribute('aria-pressed', b.classList.contains('active') ? 'true' : 'false');
    b.addEventListener('click', () => setView(state, b.dataset['repView'] ?? 'overview'));
  });
  qsa<HTMLElement>('[data-rep-go]', state.takeover).forEach((btn) => {
    btn.addEventListener('click', () => setView(state, btn.dataset['repGo'] ?? 'overview'));
  });
}

/** Liga apenas o toggle do ticker (a montagem ocorre no open) — reputation.js 422-429. */
function bindTickerControls(_state: ReputationTakeoverState): void {
  bindReputationTickerToggle();
}

/** Desenha todos os charts uma unica vez — porte FIEL de `drawCharts` (reputation.js 461-469). */
function drawCharts(state: ReputationTakeoverState): void {
  if (state.charted) return;
  state.charted = true;
  drawReputationGauge(state.takeover);
  drawReputationTrend();
  drawReputationDonut();
  drawReputationChannels();
  drawReputationNeg(REPUTATION_NEG);
}

/** Lista de focaveis visiveis para o focus-trap — porte de `visibleFocusables` (472-476). */
function visibleFocusables(takeover: HTMLElement): HTMLElement[] {
  const sel = 'a[href], button:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])';
  return qsa<HTMLElement>(sel, takeover)
    .filter((el) => el.offsetParent !== null || el === document.activeElement);
}

/** Abre o takeover — porte FIEL de `openReputation` (reputation.js 477-488). */
function openReputation(state: ReputationTakeoverState): void {
  if (!state.takeover.hidden) return;
  state.lastFocus = document.activeElement as HTMLElement | null;
  state.takeover.hidden = false;
  document.body.classList.add('reputation-open');
  state.launcher.setAttribute('aria-expanded', 'true');
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      drawCharts(state);
      buildReputationTicker(state.reduceMotion);
    });
  });
  qs<HTMLElement>('#repClose')?.focus();
}

/** Fecha o takeover — porte FIEL de `closeReputation` (reputation.js 489-496). */
function closeReputation(state: ReputationTakeoverState): void {
  if (state.takeover.hidden) return;
  state.takeover.hidden = true;
  document.body.classList.remove('reputation-open');
  state.launcher.setAttribute('aria-expanded', 'false');
  if (state.lastFocus && typeof state.lastFocus.focus === 'function') state.lastFocus.focus();
  else state.launcher.focus();
}

/** Liga launcher/close/ESC/Tab — porte de reputation.js 497-509. */
function bindOpenClose(state: ReputationTakeoverState): void {
  state.launcher.addEventListener('click', () => openReputation(state));
  qs<HTMLElement>('#repClose')?.addEventListener('click', () => closeReputation(state));
  state.takeover.addEventListener('keydown', (e) => handleTakeoverKey(e, state));
}

/** ESC fecha (stopPropagation) e Tab faz focus-trap — porte FIEL do keydown (501-509). */
function handleTakeoverKey(e: KeyboardEvent, state: ReputationTakeoverState): void {
  if (e.key === 'Escape') {
    e.stopPropagation();
    closeReputation(state);
    return;
  }
  if (e.key !== 'Tab') return;
  const nodes = visibleFocusables(state.takeover);
  if (!nodes.length) return;
  const first = nodes[0];
  const last = nodes[nodes.length - 1];
  /* v8 ignore next -- nodes já validado não-vazio acima; first/last sempre definidos (noUncheckedIndexedAccess) */
  if (!first || !last) return;
  if (e.shiftKey && document.activeElement === first) {
    e.preventDefault();
    last.focus();
  } else if (!e.shiftKey && document.activeElement === last) {
    e.preventDefault();
    first.focus();
  }
}
